import Chip from "@mui/joy/Chip";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Link as BundleLink } from "../../../models/bundle";
import { Note, NoteId } from "../../../models/note";
import { Person } from "../../../models/person";
import AlertDialogModal from "../../../shared/components/ui/AlertDialogModal";
import UnifiedDataTable, {
  createDeleteAction,
  DataTableColumn,
  ServerPagination,
} from "../../../shared/components/ui/UnifiedDataTable";
import { useValuesetCodings } from "../../../shared/hooks/useValuesetCodings";
import { unixToDayjs } from "../../../shared/utils/time";

interface ListOfNotesProps {
  notes: Note[];
  persons?: Person[];
  deleteNotes: (notes: NoteId[]) => void;
  // Server pagination props
  totalItems?: number;
  currentPageSize?: number;
  links?: BundleLink[];
  onPageChange?: (url: string) => void;
}

export const ListOfNotes = ({
  notes,
  persons,
  deleteNotes,
  totalItems,
  currentPageSize,
  links,
  onPageChange,
}: ListOfNotesProps) => {
  const [selectedNotes, setSelectedNotes] = React.useState<NoteId[]>([]);
  const [openDeleteNoteModal, setOpenDeleteNoteModal] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const navigate = useNavigate();

  // Hook to get document type display values
  const { getDisplayForCode } = useValuesetCodings("document-type");

  async function handleDeleteNote() {
    function reset() {
      setOpenDeleteNoteModal(false);
      setSelectedNotes([]);
      setDeleting(false);
    }
    if (selectedNotes.length > 0) {
      setDeleting(true);
      deleteNotes(selectedNotes);
      reset();
    } else {
      reset();
    }
  }

  const personMap = new Map<string | undefined | null, Person>();
  if (persons) {
    for (const person of persons) {
      personMap.set(person.person_id, person);
    }
  }

  const columns: DataTableColumn<Note>[] = [
    ...(personMap.size > 0
      ? [
          {
            key: "patient",
            header: "Patient",
            width: "220px",
            render: (note: Note) =>
              note.person_id ? (
                <>
                  {personMap.get(note.person_id)?.given_name}{" "}
                  {personMap.get(note.person_id)?.family_name}
                </>
              ) : (
                ""
              ),
          },
        ]
      : []),
    {
      key: "date",
      header: "Date",
      width: "130px",
      render: (note) => unixToDayjs(note.note_datetime)?.format("DD MMM YYYY") || "",
    },
    {
      key: "type",
      header: "Type",
      width: "200px",
      render: (note) => getDisplayForCode(note.note_type?.code) || "",
    },
    {
      key: "specialty",
      header: "Specialty",
      width: "180px",
      render: (note) =>
        note.note_category
          ? note.note_category?.map((coding) => (
              <Chip key={coding.code} color="neutral" size="sm" sx={{ mr: 1 }}>
                {coding.display}
              </Chip>
            ))
          : "-",
    },
    {
      key: "title",
      header: "Title",
      width: "200px",
      render: (note) => note.note_title || "",
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      render: (note) =>
        note.validation_date ? (
          <Chip color="success">Verified</Chip>
        ) : (
          <Chip color="warning">Unverified</Chip>
        ),
    },
  ];

  const actions = [
    createDeleteAction((selectedNotes: Note[]) => {
      setSelectedNotes(selectedNotes.map((n) => n.note_id));
      setOpenDeleteNoteModal(true);
    }),
  ];

  const emptyState = {
    title: "No note found",
  };

  // Create server pagination config if props are provided
  const serverPagination: ServerPagination | undefined =
    totalItems && currentPageSize && links && onPageChange
      ? {
          totalItems,
          currentPageSize,
          links,
          onPageChange,
        }
      : undefined;

  if (!notes) {
    return <div>No note found</div>;
  }

  return (
    <>
      <AlertDialogModal
        alertMessage="Are you sure you want to delete the selected note(s)?"
        open={openDeleteNoteModal}
        setOpen={setOpenDeleteNoteModal}
        handleDelete={handleDeleteNote}
        loading={deleting}
      />

      <UnifiedDataTable
        data={notes}
        columns={columns}
        getItemId={(note) => note.note_id}
        selectable={true}
        selectedItems={selectedNotes}
        onSelectionChange={(newSelected) => setSelectedNotes(newSelected as NoteId[])}
        actions={actions}
        onRowClick={(note) => navigate("/note/" + note.note_id)}
        serverPagination={serverPagination}
        pageSize={serverPagination ? undefined : 50}
        paginationThreshold={serverPagination ? undefined : 50}
        ariaLabel="notes table"
        emptyState={emptyState}
      />
    </>
  );
};
